import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import EventCreatePage from '../../../pages/EventCreatePage';
import { fillFor } from '../../wall/wallTokens';
import { T, RADIUS } from '../messagesModel';
import { prefersReducedMotion } from '../../transitions/portalMotion';
import { eventPrefill, EASE } from './planModel';
import usePageScrollLock from '../../../utils/usePageScrollLock';

const X = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
);

/**
 * The Event path's review (handoff §4, board 1h on a phone): the real event wizard, every section
 * stacked — THE BASICS · PLACE & PITCH · THE MONEY · THE EXTRAS — inside a 92%-tall bottom sheet
 * (phone, sliding up over 440ms) or a 560px centred modal (desktop, rising from .97 over 360ms).
 * One header row: REVIEW with FROM {CHAT} under it, a compact PORTAL | EVENT toggle, a close
 * button. No second form: `EventCreatePage` renders itself with the from-chat prefill and the
 * tags, and its own PUBLISH footer reads FILL IN n REQUIRED until the validator passes.
 *
 *   onPublished(eventId, event)   the wizard published — the caller posts the card and invites
 *   onPortal()                    the toggle: back to the Portal path
 */
export default function EventReviewSheet({ draft, mobile = false, onClose, onPublished, onPortal }) {
  const reduced = prefersReducedMotion();
  const prefill = useMemo(() => eventPrefill(draft), [draft]);
  usePageScrollLock(!!draft);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!draft || typeof document === 'undefined') return null;

  const kindPill = (on) => ({ height: 28, padding: '0 12px', borderRadius: RADIUS.pill, border: '2px solid transparent', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', background: on ? fillFor('on') : 'transparent', color: on ? T.white : T.ink, cursor: 'pointer', fontFamily: 'inherit' });

  return createPortal(
    <div data-plan-event-review role="dialog" aria-modal="true" aria-label="Review the event" className="pr-overlay-root font-mono" style={{ position: 'fixed', inset: 0, zIndex: 120 }}>
      <div data-plan-anim onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)', animation: reduced ? 'none' : 'prPlanFade 260ms linear both' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', alignItems: mobile ? 'flex-end' : 'center', padding: mobile ? 0 : 24 }}>
        <div
          data-plan-anim
          data-plan-review-panel
          style={{
            pointerEvents: 'auto', position: 'relative', width: '100%', maxWidth: mobile ? '100%' : 560, height: mobile ? '92%' : '100%', maxHeight: mobile ? '92%' : 'min(860px, 100%)',
            background: T.white, color: T.ink, borderRadius: mobile ? '28px 28px 0 0' : RADIUS.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            animation: reduced ? 'none' : (mobile ? `prPlanSheetUp 440ms ${EASE} both` : `prPlanModalIn 360ms ${EASE} both`),
          }}
        >
          {mobile && <span aria-hidden style={{ alignSelf: 'center', width: 40, height: 4, borderRadius: 2, background: T.dayPill, marginTop: 10, flexShrink: 0 }} />}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, padding: mobile ? '8px 16px 10px' : '18px 24px 14px' }}>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>REVIEW</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: T.grey500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>FROM {(draft.threadName || 'THE CHAT').toUpperCase()}</span>
            </span>
            <div role="radiogroup" aria-label="Make it a" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: RADIUS.pill, background: T.off, flexShrink: 0 }}>
              <button type="button" role="radio" aria-checked={false} data-plan-kind="portal" onClick={onPortal} style={kindPill(false)}>PORTAL</button>
              <button type="button" role="radio" aria-checked data-plan-kind="event" style={kindPill(true)}>EVENT</button>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: '50%', border: 0, background: T.off, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}>{X}</button>
          </div>
          <div className="hev" data-sheet="" data-compact={mobile ? '' : undefined} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'transparent' }}>
            <EventCreatePage
              embedded
              sheet
              compact={mobile}
              prefill={prefill}
              onComplete={(eventId, event) => onPublished?.(eventId, event)}
              onCancel={onClose}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
